import { BusyIndicator } from "client/Components";
import {
	AlertManager,
	createLocalDataSource,
	useAlerts,
	useIsTouchDevice,
	useSelection
} from "client/Hooks";
import {
	RecentDocumentItemViewModel,
	DocumentsViewModel,
	DocumentsViewType,
	ListLocationViewModel
} from "client/Api/Documents/SharePoint/common";
import {
	ReactElement,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { SharePointRecentlyModifiedTableView } from "./SharePointRecentlyModifiedTableView";
import { getExtension } from "client_legacy/Documents/path";
import { SharePointRecentlyModifiedListView } from "./SharePointRecentlyModifiedListView";
import { DocumentLayoutKind } from "client/Api/Documents/documents";
import { SharePointRecentlyModifiedGridView } from "./SharePointRecentlyModifiedGridView";
import { contains } from "client/Data/searching";
import { getRecentDocuments } from "client/Api/Documents/SharePoint/sharepointDocuments";
import { UserEditorPreference } from "client_legacy/Documents/officeDocumentsOpenPreference";
import { useEditorPreference } from "../../useEditorPreference";
import { SelectMode } from "client/Misc/commonTypes";
import {
	getLocationModelFromDocument,
	SharePointItemLocationViewModel
} from "client/Features/Layout/TitleBar/GlobalSearch/Items/DocumentListItem";
import {
	RecentDocumentAction,
	RecentDocumentActionContext,
	RecentDocumentActionAndOthers,
	getOpenActions
} from "./actions";
import { reduceToAvailableActions } from "client_legacy/ndaw/actions";
import { ImageFile, useImagePreviewDialog } from "../../Dialogs/ImagePreview/ImagePreviewDialog";
import { getDownloadFileUrl } from "client_legacy/Documents/SharePoint/UrlsProviders/documentsItemUrlsProviders";
import { isNotNilOrEmpty } from "client/Misc/utils";
import {
	RecentDocumentActionHandlers,
	RecentDocumentActionsWithContext,
	useRecentDocumentActions
} from "./useRecentDocumentActions";
import { generateSharePointUniqueKey } from "../SharePointOverView";

export type SharePointRecentlyModifiedProps = {
	view: DocumentsViewModel;
	isMobile: boolean;
	layoutKind: DocumentLayoutKind;
	searchTerm: VersionedValue<string>;
	isSharingViaLinkEnabled: boolean;
	onActionsChanged: (value: RecentDocumentActionsWithContext) => (() => void);
};

export const SharePointRecentlyModified = (props: SharePointRecentlyModifiedProps): ReactElement => {
	const {
		view,
		isMobile,
		layoutKind,
		searchTerm,
		isSharingViaLinkEnabled,
		onActionsChanged
	} = props;

	if (view.type !== DocumentsViewType.RecentlyModified) {
		throw new Error("Must be a recently modified view");
	}

	const [selectMode, setSelectMode] = useState<SelectMode>(SelectMode.Disabled);
	const [selectedItems, setSelectedItems] = useState<readonly GridItem[]>([]);

	const [loadResult, setLoadResult] = useState<LoadResult>({
		status: "loading"
	});

	const isTouch: boolean = useIsTouchDevice();
	const [imagePreviewDialog, openImagePreviewDialog] = useImagePreviewDialog();
	const isInSelectMode: boolean = selectMode !== SelectMode.Disabled;
	const hasSearch: boolean = isNotNilOrEmpty(searchTerm.value);

	const items = useMemo(
		(): readonly GridItem[] => {
			return loadResult.status === "successful"
				? loadResult.result
				: [];
		},
		[loadResult]
	);

	const { currentItems } = useDataSource(
		items,
		{
			searchTerm: searchTerm.value
		}
	);

	const { selectionFor, multiSelection } = useSelection(
		items,
		"uniqueKey",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems
		}
	);

	const location: ListLocationViewModel = view.location;

	const alertManager: AlertManager = useAlerts();

	useEffect(
		(): (() => void) => {
			async function fetchData(
				signal: AbortSignal
			): Promise<void> {
				setLoadResult({
					status: "loading"
				});

				try {
					const documents: readonly RecentDocumentItemViewModel[] = await getRecentDocuments(
						signal
					);

					setLoadResult({
						status: "successful",
						result: documents.map(convertToGridItem)
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					setLoadResult({
						status: "error"
					});

					console.error(e);
					alertManager.showError(localize("_UnableToRetrieveItems"));
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);
			return () => cancellation.abort();
		},
		[location, alertManager]
	);

	const editorPreference: UserEditorPreference = useEditorPreference();

	const actionHandlers = useMemo(
		(): RecentDocumentActionHandlers => ({
			selectAll: () => setSelectedItems(items),
			deselectAll: () => setSelectedItems([]),
			disableSelectMode: () => setSelectMode(SelectMode.Disabled),
			enableSelectMode: () => setSelectMode(SelectMode.Manual),
			openImageDialog: async (item: RecentDocumentItemViewModel): Promise<void> => {
				await openImagePreviewDialog({
					currentImage: convertRecentDocumentToImageFile(item),
					actions: [],
					context: {}
				});
			}
		}),
		[
			items,
			openImagePreviewDialog
		]
	);

	const [
		actionContext,
		selectionActions,
		listActions
	] = useRecentDocumentActions({
		alertManager,
		allItems: items,
		editorPreference,
		handlers: actionHandlers,
		isInSelectMode,
		isMobile,
		isSharingViaLinkEnabled,
		selectedItems
	});

	const allActions = useMemo(
		(): readonly RecentDocumentActionAndOthers[] => [...listActions, ...selectionActions],
		[listActions, selectionActions]
	);

	useEffect(
		(): (() => void) => {
			return onActionsChanged({
				context: actionContext,
				actions: allActions
			});
		},
		[
			allActions,
			actionContext,
			onActionsChanged
		]
	);

	const openItemHandler = (item: RecentDocumentItemViewModel): void => {
		const context: RecentDocumentActionContext = {
			...actionContext,
			selection: [item]
		};

		const actions: readonly RecentDocumentAction[] = getOpenActions({
			alertManager,
			editorPreference,
			handlers: actionHandlers
		});

		reduceToAvailableActions(actions, context)[0]
			?.execute(context);
	};

	const renderContent = (): ReactElement | null => {
		if (loadResult.status !== "successful") {
			return null;
		}

		if (layoutKind === DocumentLayoutKind.Grid) {
			return (
				<SharePointRecentlyModifiedGridView
					items={currentItems}
					multiSelection={multiSelection}
					selectionActions={selectionActions}
					actionContext={actionContext}
					selectionFor={selectionFor}
					onOpenItem={openItemHandler}
					onEnableSelectMode={() => setSelectMode(SelectMode.FromLongTouch)}
					isInSelectMode={isInSelectMode}
					isMobile={isMobile}
					isTouch={isTouch}
					hasSearch={hasSearch}
				/>
			);
		}

		return (
			<>
				{isMobile
					? (
						<SharePointRecentlyModifiedListView
							items={currentItems}
							selectionFor={selectionFor}
							selectionActions={selectionActions}
							actionContext={actionContext}
							onOpenItem={openItemHandler}
							onEnableSelectMode={() => setSelectMode(SelectMode.FromLongTouch)}
							isInSelectMode={isInSelectMode}
							hasSearch={hasSearch}
						/>
					): (
						<SharePointRecentlyModifiedTableView
							items={currentItems}
							multiSelection={multiSelection}
							selectionActions={selectionActions}
							actionContext={actionContext}
							selectionFor={selectionFor}
							onOpenItem={openItemHandler}
							isTouch={isTouch}
							hasSearch={hasSearch}
						/>
					)
				}
			</>
		);
	};

	return (
		<>
			{loadResult.status === "loading" && (
				<BusyIndicator />
			)}
			{renderContent()}
			{imagePreviewDialog}
		</>
	);
};
SharePointRecentlyModified.displayName = "SharePointRecentlyModified";

type LoadResult = {
	status: "loading";
} | {
	status: "error";
} | {
	status: "successful";
	result: readonly GridItem[];
};

export type GridItem = {
	id: number;
	uniqueKey: string;
	name: string;
	modifiedBy: string | null;
	createdBy: string | null;
	modifiedOn: string | null;
	size: number | null;
	extension: string | null;
	originalLocation: SharePointItemLocationViewModel;
	model: RecentDocumentItemViewModel;
};

const useDataSource = createLocalDataSource<GridItem>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) => contains(item.name, searchTerm)
		},
	})
	.getHook();

function convertToGridItem(document: RecentDocumentItemViewModel): GridItem {
	const {
		id,
		name,
		modifiedBy,
		createdBy,
		modifiedOn,
		size,
		location
	} = document;

	return {
		id,
		uniqueKey: generateSharePointUniqueKey(id, location),
		name,
		modifiedBy,
		createdBy,
		modifiedOn,
		size,
		extension: getExtension(name),
		originalLocation: getLocationModelFromDocument(document),
		model: document
	};
}

export function convertRecentDocumentToImageFile(
	item: RecentDocumentItemViewModel
): ImageFile {
	return {
		id: item.id,
		name: item.name,
		url: getDownloadFileUrl(item.id, item.location)
	};
}
